import { CheckLoginUseCase } from '@/core/domain/auth/usecases/check-login.usecase';
import { LoginUseCase } from '@/core/domain/auth/usecases/login.usecase';
import { PostLoginUseCase } from '@/core/domain/auth/usecases/post-login.usecase';
import { GtmError } from '@/core/domain/gtm/models/gtm-error.models';
import { SetDataLayerParamsUseCase } from '@/core/domain/gtm/usecases/setDataLayer.usecase';
import { ModalErrorOpenUseCase } from '@/core/domain/modal-error/usecases/modal-error-open.usecase';
import * as actions from '@/data/auth/store/auth.actions';
import { selectAuthValue } from '@/data/auth/store/auth.selectors';
import { selectStateValue } from '@/data/auth/store/state.selectors';
import { inject } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivateFn,
    Router,
    RouterStateSnapshot,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { catchError, first, of, switchMap } from 'rxjs';

export const DashboardGuard: CanActivateFn = (
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
) => {
    const authLogin = inject(LoginUseCase);
    const authPostLogin = inject(PostLoginUseCase);
    const authCheckLogin = inject(CheckLoginUseCase);
    const modalError = inject(ModalErrorOpenUseCase);
    const router = inject(Router);
    const store = inject(Store);
    const translateService = inject(TranslateService);
    const gtmDataLayer = inject(SetDataLayerParamsUseCase);

    const qp = state.root.queryParams as any;
    if (Object.keys(qp).length !== 0) {
        if (qp?.error || qp?.error_description) {
            modalError.execute({
                error: translateService.instant(
                    'vg_sso_vg_modal_wrapper_p_title',
                ),
                description:
                    translateService.instant(
                        'vg_sso_vg_modal_wrapper_p_descripcion',
                    ) +
                    ' ' +
                    translateService.instant(
                        'vg_sso_vg_modal_wrapper_p_contact',
                    ),
                redirectSSO: false,
            });

            router.navigate(['/']);

            return of(false);
        }

        if (!qp?.activation_code) {
            return of(false);
        }
        return store.select(selectStateValue).pipe(
            first(),
            switchMap((state) => {
                if (state !== qp?.state) {
                    return of(false);
                }
                return store.select(selectAuthValue).pipe(
                    first(),
                    switchMap((auth) => {
                        if (auth.isLogged) {
                            return of(true);
                        }

                        return authPostLogin.execute(qp).pipe(
                            switchMap((responsePostLogin) => {
                                if (
                                    responsePostLogin.type === 'ERROR' &&
                                    responsePostLogin.code &&
                                    responsePostLogin.message
                                ) {
                                    const translatedCode =
                                        translateService.instant(
                                            'vg_sso_vg_modal_wrapper_p_descripcion',
                                        );
                                    gtmDataLayer.execute<GtmError>({
                                        event: 'ev_message',
                                        message: {
                                            type: `${responsePostLogin.type} | ${translatedCode}`,
                                            code: responsePostLogin.code,
                                            message: responsePostLogin.message,
                                        },
                                    });
                                }
                                return of(
                                    responsePostLogin.type === 'SUCCESS' ||
                                        (responsePostLogin.type === 'WARNING' &&
                                            responsePostLogin.action ===
                                                'CONTINUE'),
                                );
                            }),
                            switchMap((isOk) => {
                                if (isOk === true) {
                                    return of(isOk);
                                }
                                store.dispatch(actions.resetToken());

                                modalError.execute({
                                    error: translateService.instant(
                                        'vg_sso_vg_modal_wrapper_p_title',
                                    ),
                                    description:
                                        translateService.instant(
                                            'vg_sso_vg_modal_wrapper_p_descripcion',
                                        ) +
                                        ' ' +
                                        translateService.instant(
                                            'vg_sso_vg_modal_wrapper_p_contact',
                                        ),
                                    redirectSSO: false,
                                });

                                router.navigate(['/']);

                                return of(false);
                            }),
                            catchError((error) => {
                                console.error('Error occurred:', error);

                                store.dispatch(actions.resetToken());

                                modalError.execute({
                                    error: translateService.instant(
                                        'vg_sso_vg_modal_wrapper_p_title',
                                    ),
                                    description:
                                        translateService.instant(
                                            'vg_sso_vg_modal_wrapper_p_descripcion',
                                        ) +
                                        ' ' +
                                        translateService.instant(
                                            'vg_sso_vg_modal_wrapper_p_contact',
                                        ),
                                    redirectSSO: false,
                                });

                                const translatedCode = translateService.instant(
                                    'vg_sso_vg_modal_wrapper_p_descripcion',
                                );
                                gtmDataLayer.execute<GtmError>({
                                    event: 'ev_message',
                                    message: {
                                        type: `${error.type} | ${translatedCode}`,
                                        code: error.code,
                                        message: error.message,
                                    },
                                });
                                router.navigate(['/']);

                                return of(false);
                            }),
                        );
                    }),
                );
            }),
            catchError((error) => {
                console.error('Error occurred:', error);
                return of(false);
            }),
        );
    }

    return authCheckLogin.execute().pipe(
        switchMap((auth) => {
            if (!auth?.isLogged) {
                authLogin.execute({ path: 'dashboard/home' });
                return of(false);
            }

            return of(true);
        }),
    );
};
